import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que Recopilamos',
    text: 'Recopilamos información personal que nos proporcionas voluntariamente al realizar una compra, como tu nombre, dirección de correo electrónico y datos de pago. No recopilamos información de menores de edad directamente.'
  }, {
    heading: 'Uso de la Información',
    text: 'Utilizamos tu información personal únicamente para procesar tu compra, enviarte el producto digital adquirido y comunicarnos contigo sobre tu pedido. No vendemos, alquilamos ni compartimos tu información personal con terceros con fines de marketing.'
  }, {
    heading: 'Protección de Datos',
    text: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción.'
  }, {
    heading: 'Cookies',
    text: 'Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación y analizar el tráfico del sitio. Puedes configurar tu navegador para rechazar cookies, aunque esto puede afectar la funcionalidad del sitio.'
  }, {
    heading: 'Tus Derechos',
    text: 'Tienes derecho a acceder, corregir o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, contáctanos a través de los canales indicados en tu confirmación de compra.'
  }, {
    heading: 'Cambios a esta Política',
    text: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Te notificaremos sobre cambios significativos a través del correo electrónico asociado a tu compra.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Aceptación de los Términos',
    text: 'Al acceder y utilizar este sitio web y adquirir nuestros productos digitales, aceptas estar sujeto a estos términos y condiciones de uso.'
  }, {
    heading: 'Producto Digital',
    text: 'El Botiquín Natural de Emergencia es un producto digital informativo. El contenido se entrega en formato digital inmediatamente después de la compra. No se realizan envíos físicos.'
  }, {
    heading: 'Uso del Contenido',
    text: 'El contenido adquirido es para uso personal y familiar únicamente. Queda prohibida la reproducción, distribución, modificación o reventa del material sin autorización expresa por escrito.'
  }, {
    heading: 'Descargo de Responsabilidad Médica',
    text: 'La información proporcionada en este manual es de carácter educativo e informativo. No sustituye el consejo, diagnóstico o tratamiento médico profesional. Siempre consulta con un profesional de la salud calificado antes de tomar decisiones sobre la salud de tu hijo.'
  }, {
    heading: 'Política de Reembolso',
    text: 'Debido a la naturaleza digital del producto y su entrega inmediata, todas las ventas son finales. Si experimentas problemas técnicos para acceder al contenido, contáctanos y te ayudaremos a resolverlos.'
  }, {
    heading: 'Limitación de Responsabilidad',
    text: 'No nos hacemos responsables de los resultados obtenidos al aplicar la información contenida en el manual. El uso del contenido es bajo tu propia responsabilidad y criterio.'
  }, {
    heading: 'Ley Aplicable',
    text: 'Estos términos se rigen por las leyes de los Estados Unidos Mexicanos. Cualquier disputa será resuelta en los tribunales competentes de dicha jurisdicción.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const openModal = (type: ModalType) => {
    setActiveModal(type);
    document.body.style.overflow = 'hidden';
  };
  const closeModal = () => {
    setActiveModal(null);
    document.body.style.overflow = '';
  };
  const modalData = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="py-10 border-t border-brown-body/10 bg-cream-dark/30" data-id="element-47">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-48">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-sm text-brown-body/60" data-id="element-49">
            <span className="font-lora" data-id="element-50">
              © {new Date().getFullYear()} El Botiquín Natural
            </span>
            <span className="hidden sm:inline text-brown-body/20" data-id="element-51">·</span>
            <button onClick={() => openModal('privacy')} className="hover:text-sage transition-colors duration-200 font-lora underline underline-offset-2 decoration-brown-body/20 hover:decoration-sage" data-id="element-52">
              Política de Privacidad
            </button>
            <span className="hidden sm:inline text-brown-body/20" data-id="element-53">·</span>
            <button onClick={() => openModal('terms')} className="hover:text-sage transition-colors duration-200 font-lora underline underline-offset-2 decoration-brown-body/20 hover:decoration-sage" data-id="element-54">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-55">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brown-heading/50 backdrop-blur-sm" onClick={closeModal} data-id="element-56">
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} onClick={(e: React.MouseEvent) => e.stopPropagation()} className="bg-cream w-full max-w-2xl max-h-[80vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col" data-id="element-57">
              {/* Header */}
              <div className="flex items-center justify-between px-8 py-6 border-b border-brown-body/10 flex-shrink-0" data-id="element-58">
                <h3 className="text-2xl font-bold font-playfair text-brown-heading" data-id="element-59">
                  {modalData.title}
                </h3>
                <button onClick={closeModal} className="w-9 h-9 rounded-full bg-brown-body/5 hover:bg-brown-body/10 flex items-center justify-center transition-colors duration-200" aria-label="Cerrar" data-id="element-60">
                  <X className="w-5 h-5 text-brown-body/60" data-id="element-61" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto px-8 py-6 space-y-6" data-id="element-62">
                {modalData.sections.map((section, index) => <div key={index} data-id="element-63">
                    <h4 className="text-lg font-semibold font-playfair text-brown-heading mb-2" data-id="element-64">
                      {section.heading}
                    </h4>
                    <p className="text-brown-body/80 font-lora leading-relaxed" data-id="element-65">
                      {section.text}
                    </p>
                  </div>)}
              </div>

              {/* Footer */}
              <div className="px-8 py-4 border-t border-brown-body/10 flex-shrink-0" data-id="element-66">
                <button onClick={closeModal} className="w-full py-3 text-sm font-medium text-white bg-sage hover:bg-sage-dark rounded-lg transition-colors duration-200" data-id="element-67">
                  Cerrar
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}